import { useCallback, useEffect, useState } from "react";
import { fetchWithAuth, getNotificationAPI, type AccessTokenGetter } from "@/lib/utils";

/**
 * Manages the Web Push subscription lifecycle for the background
 * notification system.
 *
 * This is SEPARATE from the existing foreground polling system
 * (use-notifications.ts). The polling system drives sidebar indicators
 * and in-tab notifications. This hook manages the server-push
 * subscription that powers system-level notifications when the tab is
 * closed or the app is installed.
 *
 * The hook exposes:
 * - `permission` — the Notification.permission state
 * - `pushEnabled` — whether the user has a push subscription active
 * - `enable()` — request permission + subscribe to push + save to server
 * - `disable()` — unsubscribe from push + remove from server
 * - `isSupported` — whether the browser supports service workers + push
 */

export interface PushSubscriptionAPI {
  isSupported: boolean;
  permission: NotificationPermission;
  pushEnabled: boolean;
  enabling: boolean;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

/**
 * Revoke the browser's current endpoint with the authenticated server before
 * clearing the local PushManager subscription. Exported for the logout path,
 * where the normal hook instance may no longer be safe to use after AuthKit
 * clears its token.
 */
export async function revokeCurrentPushSubscription(
  getAccessToken: AccessTokenGetter,
): Promise<void> {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window)
  ) return;

  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;

    const response = await fetchWithAuth(
      "/api/notifications/unsubscribe",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ endpoint: sub.endpoint }),
      },
      getAccessToken,
    );
    if (!response.ok) {
      console.warn("[usePushSubscription] Failed to revoke server subscription:", response.status);
    }

    // Clear the local endpoint even if the server is temporarily unreachable.
    // A later authenticated enable() always rebinds it transactionally.
    await sub.unsubscribe();
  } catch (err) {
    console.error("[usePushSubscription] Failed to revoke subscription:", err);
  }
}

export function usePushSubscription(
  getAccessToken: AccessTokenGetter,
): PushSubscriptionAPI {
  const [isSupported] = useState(
    () =>
      getNotificationAPI() !== undefined &&
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window,
  );
  const [permission, setPermission] = useState<NotificationPermission>(
    () => getNotificationAPI()?.permission ?? "default",
  );
  const [pushEnabled, setPushEnabled] = useState(false);
  const [enabling, setEnabling] = useState(false);

  // Check on mount whether a push subscription already exists.
  useEffect(() => {
    if (!isSupported) return;
    let cancelled = false;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (!cancelled) setPushEnabled(sub !== null);
      } catch {
        // SW not ready yet — fine, will be checked again on enable
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSupported]);

  const enable = useCallback(async () => {
    if (!isSupported || enabling) return;
    const NotificationAPI = getNotificationAPI();
    if (!NotificationAPI) return;
    setEnabling(true);
    try {
      // 1. Request notification permission
      if (NotificationAPI.permission === "default") {
        const result = await NotificationAPI.requestPermission();
        setPermission(result);
        if (result !== "granted") return;
      } else if (NotificationAPI.permission !== "granted") {
        return;
      }

      // 2. Register the service worker (idempotent)
      try {
        await navigator.serviceWorker.register("/service-worker.js");
      } catch {
        console.error("[usePushSubscription] SW registration failed");
        return;
      }
      const reg = await navigator.serviceWorker.ready;

      // 3. Fetch the public VAPID key. This endpoint deliberately has no
      //    user-specific data; the subsequent subscription mutation is
      //    authenticated through fetchWithAuth below.
      const vapidRes = await fetch("/api/notifications/vapid-public");
      if (!vapidRes.ok) {
        console.error("[usePushSubscription] Failed to fetch VAPID key");
        return;
      }
      const vapidData = await vapidRes.json();
      const vapidKey = vapidData.public_key;
      if (!vapidKey) return;

      // 4. Check for an existing browser-side subscription.
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        // 5. No browser subscription — create one with the server's VAPID key.
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidKey,
        });
      }

      // 6. Always (re-)save the subscription on the server. The server makes
      //    endpoint ownership globally exclusive, so this authenticated write
      //    also rebinds a browser profile after an account/tenant switch while
      //    healing desync caused by:
      //    - The server row being pruned after a 410/404/401 push failure
      //    - A chat-bridge restart that lost the DB row
      //    - A VAPID key rotation that invalidated the old row
      //    Without this, the browser reports pushEnabled=true from the
      //    PushManager check alone, the auto-subscribe effect in main.tsx
      //    never re-fires, and the server silently has 0 rows for the user.
      const payload = {
        endpoint: sub.endpoint,
        keys: {
          p256dh: arrayBufferToBase64URL(sub.getKey("p256dh")!),
          auth: arrayBufferToBase64URL(sub.getKey("auth")!),
        },
      };
      const saveRes = await fetchWithAuth(
        "/api/notifications/subscribe",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        getAccessToken,
      );
      if (!saveRes.ok) {
        console.error(
          "[usePushSubscription] Failed to save subscription:",
          saveRes.status,
        );
        // Unsubscribe locally since the server didn't accept it
        await sub.unsubscribe();
        // A browser-side endpoint alone is not a usable notification
        // subscription. Keep the settings toggle honest after a 401/403 or
        // transient server failure instead of reporting "Notifications on"
        // for an endpoint the delivery worker cannot look up.
        setPushEnabled(false);
        return;
      }

      setPushEnabled(true);
      console.log("[usePushSubscription] Push notifications enabled");
    } catch (err) {
      console.error("[usePushSubscription] Enable failed:", err);
      setPushEnabled(false);
    } finally {
      setEnabling(false);
    }
  }, [isSupported, enabling, getAccessToken]);

  const disable = useCallback(async () => {
    if (!isSupported) return;
    await revokeCurrentPushSubscription(getAccessToken);
    setPushEnabled(false);
    console.log("[usePushSubscription] Push notifications disabled");
  }, [isSupported, getAccessToken]);

  return {
    isSupported,
    permission,
    pushEnabled,
    enabling,
    enable,
    disable,
  };
}

function arrayBufferToBase64URL(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}
